// =====================================================
// CLASIFICACIÓN CONTABLE - Constantes compartidas
// =====================================================

// Cuentas del Estado de Resultados para clasificar gastos
export const CUENTAS_ER = [
  // Costos Fijos
  { value: "oficina", label: "Oficina", grupo: "Costos Fijos" },
  { value: "transporte", label: "Transporte", grupo: "Costos Fijos" },
  { value: "telefonia", label: "Telefonía", grupo: "Costos Fijos" },
  { value: "marketing", label: "Marketing", grupo: "Costos Fijos" },
  { value: "contador", label: "Contador", grupo: "Costos Fijos" },
  { value: "abogados", label: "Abogados", grupo: "Costos Fijos" },
  { value: "gastos_representacion", label: "Gastos representación", grupo: "Costos Fijos" },
  { value: "asesoria_externa", label: "Asesoría externa", grupo: "Costos Fijos" },
  { value: "alojamiento", label: "Alojamiento", grupo: "Costos Fijos" },
  { value: "viajes_gerencia", label: "Viajes Gerencia", grupo: "Costos Fijos" },
  { value: "gastos_bancarios", label: "Gastos Financieros", grupo: "Costos Fijos" },
  { value: "rrhh", label: "RRHH", grupo: "Costos Fijos" },
  // Costo financiero de factoring con recurso (NIC 23.5/23.6(a), NIIF 9 B5.4.1) — grupo
  // DISTINTO de "Costos Fijos" a propósito: el ER v2 (ERv2/index.js) las suma a la línea
  // "Intereses" DEBAJO del EBITDA, nunca a camposCostosFijosV2/CAMPOS_COSTOS_FIJOS. Ver
  // docs/modulos/er-v2.md y regla 10.277.
  { value: "factoring_comision", label: "Factoring — Comisión", grupo: "Costo Financiero" },
  { value: "factoring_mora", label: "Factoring — Mora", grupo: "Costo Financiero" },
  { value: "pago_impuesto", label: "Impuestos", grupo: "Costos Fijos" },
  { value: "servicios_online", label: "Servicios Online", grupo: "Costos Fijos" },
  { value: "contenidos_digitales", label: "Contenidos Digitales", grupo: "Costos Fijos" },
  { value: "varios", label: "Varios", grupo: "Costos Fijos" },
  // Cuenta huérfana para reversos de tarjeta sin compra apareada (mecanismo de
  // reversos-webpay-diseno-2026-09-20.md §1.1/1.3). Sin DDL — cuenta_er es text libre en
  // movimientos_caja, validado solo del lado del cliente contra esta constante.
  { value: "ajustes_tarjeta", label: "Ajustes de tarjeta", grupo: "Costos Fijos" },
  // Costos Variables
  { value: "hardware", label: "Hardware", grupo: "Costos Variables" },
  { value: "software_ds", label: "Software DS", grupo: "Costos Variables" },
  { value: "mantenimiento", label: "Mantenimiento", grupo: "Costos Variables" },
  { value: "servicios_internet", label: "Servicios Internet", grupo: "Costos Variables" },
];

// Tipos de activo (extraídos de GestionActivos)
export const TIPOS_ACTIVO = [
  { value: "accesorios", label: "Accesorios", vidaUtil: 60, esIntangible: false },
  { value: "antena", label: "Antena", vidaUtil: 60, esIntangible: false },
  { value: "computador", label: "Computador", vidaUtil: 36, esIntangible: false },
  { value: "costo_instalacion", label: "Implementación", vidaUtil: 60, esIntangible: false },
  { value: "herramientas", label: "Herramientas", vidaUtil: 60, esIntangible: false },
  { value: "insumos_instalacion", label: "Insumos", vidaUtil: 60, esIntangible: false },
  { value: "investigacion", label: "Investigación", vidaUtil: 24, esIntangible: true },
  { value: "licencia", label: "Licencia", vidaUtil: 36, esIntangible: true },
  { value: "marca", label: "Marca", vidaUtil: 120, esIntangible: true },
  { value: "pantalla", label: "Pantalla", vidaUtil: 60, esIntangible: false },
  { value: "patente", label: "Patente", vidaUtil: 120, esIntangible: true },
  { value: "reproductor", label: "Reproductor", vidaUtil: 60, esIntangible: false },
  { value: "smartphone", label: "Smartphone", vidaUtil: 24, esIntangible: false },
  { value: "software", label: "Software", vidaUtil: 36, esIntangible: true },
  { value: "soporte", label: "Soporte", vidaUtil: 60, esIntangible: false },
  { value: "totem", label: "Tótem", vidaUtil: 60, esIntangible: false },
  { value: "vestimenta", label: "Vestimenta", vidaUtil: 24, esIntangible: false },
];

// Label "bonito" de un tipo de activo (Tótem, Implementación, Soporte, …). Fuente ÚNICA (10.1),
// derivada de TIPOS_ACTIVO. La consumen la columna Tipo del modal Inversión y el concepto CapEx del
// flujo. Fallback para un value sin entrada: guiones→espacios + capitalizado; "—" si viene vacío.
export const TIPO_ACTIVO_LABELS = Object.fromEntries(TIPOS_ACTIVO.map((t) => [t.value, t.label]));

export const getTipoActivoLabel = (value) =>
  TIPO_ACTIVO_LABELS[value] || (value ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

// Tipos que se serializan 1-fila-por-unidad (equipos con serie de fábrica e identidad
// propia). Todo lo que NO esté acá — consumibles (insumos, herramientas), partes pasivas
// (soportes) y servicios (costo_instalacion) — va como 1 sola fila con `cantidad`.
// Motivo: explotar consumibles generaría cientos de filas sin valor (p.ej. 965 unidades de
// insumos_instalacion hoy), y un servicio no tiene unidad física que serializar.
// Ver bloque Stock (ARQUITECTURA 9.36). Los values coinciden con TIPOS_ACTIVO.
export const TIPOS_SERIALIZABLES = ["pantalla", "reproductor", "totem", "antena", "computador", "smartphone"];

export const esSerializablePorTipo = (tipo) => TIPOS_SERIALIZABLES.includes(tipo);

// Destinos posibles para activos
export const DESTINO_TIPOS = [
  { value: "", label: "Sin asignar" },
  { value: "edificio", label: "Edificio" },
  { value: "carteleria", label: "Proyecto cartelería" },
  { value: "empleado", label: "Empleado" },
];

// Helpers
export const getVidaUtilPorTipo = (tipo) => {
  const found = TIPOS_ACTIVO.find((t) => t.value === tipo);
  return found ? found.vidaUtil : 60;
};

export const esIntangiblePorTipo = (tipo) => {
  const found = TIPOS_ACTIVO.find((t) => t.value === tipo);
  return found ? found.esIntangible : false;
};
